import React from 'react';
import { Box, Typography } from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import { FONT_ENUM } from '../../types/global/fonts.types';
import type { IDeliverable } from '../../redux/buzzbridge/types';
import { deliverableLabel, resolveDeliverablePlatform } from '../../utils/deliverableLabels';
import { InstagramGlyph, PLATFORM_BRAND, TiktokGlyph, YoutubeGlyph } from './SocialPlatformSvgs';

const ICON_SX = { width: 16, height: 16 };

function DeliverablePlatformIcon({ deliverable }: { deliverable: IDeliverable }) {
    const platform = resolveDeliverablePlatform(deliverable);
    switch (platform) {
        case 'instagram':
            return <InstagramGlyph sx={ICON_SX} color={PLATFORM_BRAND.instagram.primary} />;
        case 'tiktok':
            return <TiktokGlyph sx={ICON_SX} color={PLATFORM_BRAND.tiktok.primary} />;
        case 'youtube':
            return <YoutubeGlyph sx={ICON_SX} color={PLATFORM_BRAND.youtube.primary} />;
        case 'twitter':
            return <XIcon sx={{ fontSize: 16, color: '#fff' }} />;
        case 'facebook':
            return <FacebookIcon sx={{ fontSize: 16, color: '#1877F2' }} />;
        default:
            return <AssignmentOutlinedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.55)' }} />;
    }
}

interface DeliverableBulletListProps {
    deliverables: IDeliverable[];
    /** Body text color */
    textColor?: string;
}

/** Platform glyph as bullet + deliverable label — shared by brand management + creator details. */
export function DeliverableBulletList({
    deliverables,
    textColor = 'rgba(255,255,255,0.78)',
}: DeliverableBulletListProps) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {deliverables.map((d, i) => (
                <Box key={`${d.type}-${i}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ flexShrink: 0, mt: '2px', display: 'inline-flex', lineHeight: 0 }}>
                        <DeliverablePlatformIcon deliverable={d} />
                    </Box>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 14,
                            color: textColor,
                            lineHeight: 1.6,
                        }}>
                        {deliverableLabel(d)}
                        {d.duration_range && d.duration_range !== 'n/a' ? ` · ${d.duration_range}` : ''}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}
